import { logNotification, makeStringDataField } from '@microsoft/oteljs';
import * as MinSink from './MinSink';
import { getCurrentMilliseconds, getEmptyStats, isNumeric } from './Utils';
var DEFAULT_BATCH_INTERVAL = 1000; // ms
var DEFAULT_QUEUE_SIZE = 500;
var SINK_NAME = 'WebSink';
function logWebSinkError(error) {
    logWebSinkNotification(0 /* Error */, error);
}
function logWebSinkWarning(error) {
    logWebSinkNotification(1 /* Warning */, error);
}
function logWebSinkNotification(logLevel, error) {
    logNotification(logLevel, 1 /* Sink */, function () { return SINK_NAME + ':' + error; });
}
/**
 * A sink that sends telemetry to One Collector using a 1DS sink inside a web worker,
 * with a minimal sink as a fallback for shutdown.
 */
var WebSink = /** @class */ (function () {
    /**
     * Creates a WebSink
     * The sink can immediately begin accepting events, but
     * the caller must call init() before the sink will send events
     * The caller should also call startWorker() to enable the web worker
     *
     * Canonical usage
     * webSink = new WebSink(coreProps);
     * // Immediately attach any samplers to ensure sampling is applied consistently
     * webSink.preprocessors.push(new Sampler());
     * // At a convenient time, init the sink with props.
     * webSink.init(props, utils);
     * // At a convenient time, spawn the web worker. If this step is skipped, events will only be sent
     * // at shutdown via the MinSink, and the number of events will be limited.
     * webSink.startWorker();
     * // A throttler can be added at construction or later on.
     * webSink.preprocessors.push(new EventThrottler());
     * // Send outstanding events via the MinSink
     * webSink.shutdown();
     * @param coreProps - Essential props for initializing the sink
     */
    function WebSink(coreProps) {
        /**
         * Preprocessors to attach to the web sink.
         */
        this.preprocessors = [];
        /**
         * Stats about the web sink; these can be read at any time
         */
        this.stats = getEmptyStats();
        this._batchInterval = DEFAULT_BATCH_INTERVAL;
        /**
         * Before the web worker is initialized, this is the number of events that we'll store in the queue
         * until we give up and send events via the MinSink.
         * These events are not guaranteed to be sent - if the queue does not fill up, and shutdown() is called,
         * then MinSink will attempt to send the events via sendBeacon, which has a 64KB limit.
         * After the web worker is initialized, the queue size limit is ignored - the throttler should be used
         * to limit event volume.
         */
        this._queueSize = DEFAULT_QUEUE_SIZE;
        this._queue = [];
        /**
         * Whether this sink is still capable of handling events.
         * Becomes false after the WebWorker fails to load or shutdown is called.
         */
        this.enabled = true;
        if (coreProps && coreProps.queueSize) {
            this._queueSize = coreProps.queueSize;
        }
    }
    /**
     * Initialize the web sink
     * @param props - Properties for initializing the web sink, which are passed to the underlying sink.
     * The props object gets serialized and sent to the web worker, so it must include no functions.
     * @param utils - Callback functions used by the web sink.
     * @returns - Whether initialization succeeded
     */
    WebSink.prototype.init = function (props, utils) {
        if (!props.endpointUrl || !utils.uuid || !utils.getWorker) {
            this.enabled = false;
            logWebSinkError(2 /* Initialization */);
            return false;
        }
        if (props.uploadFrequency) {
            this._batchInterval = props.uploadFrequency;
        }
        this._props = props;
        this._utils = utils;
        return true;
    };
    /**
     * Checks if the sink is initialized, and logs a warning if not.
     * Even if the sink is initialized, the web worker could still be uninitialized.
     * @returns Is the sink initialized
     */
    WebSink.prototype.isInitialized = function () {
        if (this.enabled) {
            if (this._props) {
                return true;
            }
            logWebSinkWarning(1 /* Uninitialized */);
        }
        return false;
    };
    WebSink.prototype.mergeStats = function (stats) {
        var _this = this;
        Object.keys(stats).forEach(function (key) {
            if (isNumeric(_this.stats[key])) {
                if (isNumeric(stats[key])) {
                    _this.stats[key] += stats[key];
                }
                else {
                    _this.stats[key] = stats[key];
                }
            }
            else if (key === "wr" /* WebWorkerDroppedReason */.toString()) {
                Object.keys(stats["wr" /* WebWorkerDroppedReason */]).forEach(function (reason) {
                    _this.stats["wr" /* WebWorkerDroppedReason */][reason] =
                        _this.stats["wr" /* WebWorkerDroppedReason */][reason] + stats["wr" /* WebWorkerDroppedReason */] ||
                            stats["wr" /* WebWorkerDroppedReason */][reason];
                });
            }
        });
    };
    /**
     * Attempt to start the web worker thread
     * When the worker loads, it sends a WebWorkerMessageType.Loaded message.
     * The main thread responds by sending an Initialize message with parameters, followed by any queued events.
     * @returns - nothing
     */
    WebSink.prototype.startWorker = function () {
        var _this = this;
        if (!this.isInitialized()) {
            return;
        }
        this.stats["s" /* WebWorkerStatus */] = 1 /* Initializing */;
        try {
            this._worker = this._utils.getWorker();
            this._worker.addEventListener('message', function (e) {
                var data = e.data;
                switch (data.t) {
                    case 1 /* Loaded */:
                        _this.handleLoadedMessage();
                        break;
                    case 4 /* Stats */:
                        _this.mergeStats(data.d);
                        break;
                    case 5 /* Error */:
                        logWebSinkWarning(data.d);
                        break;
                    default:
                        logWebSinkWarning(14 /* WorkerUnhandledMessage */);
                }
            });
            this._worker.addEventListener('error', function (_e) {
                // The error isn't safe to log, as we don't know what it contains
                logWebSinkWarning(12 /* WorkerError */);
                // A failure while initializing is terminal.
                if (_this.stats["s" /* WebWorkerStatus */] === 1 /* Initializing */) {
                    _this.failToStartWorker();
                }
            });
            this._worker.addEventListener('errormessage', function (_e) {
                // The errormessage isn't safe to log, as we don't know what it contains
                logWebSinkWarning(13 /* WorkerErrorMessage */);
            });
        }
        catch (_e) {
            logWebSinkWarning(10 /* WorkerInitialization */);
            this.failToStartWorker();
        }
    };
    WebSink.prototype.handleLoadedMessage = function () {
        if (!this.isInitialized()) {
            return;
        }
        this.stats["s" /* WebWorkerStatus */] = 2 /* Initialized */;
        logNotification(2 /* Info */, 1 /* Sink */, function () { return SINK_NAME + ':Loaded'; });
        var initMessage = {
            t: 2 /* Initialize */,
            // Props is checked to exist in ensureInitialized
            d: this._props
        };
        this._worker.postMessage(initMessage);
        // Start sending messages
        this.flushQueue();
    };
    WebSink.prototype.failToStartWorker = function () {
        this.stats["s" /* WebWorkerStatus */] = 4 /* Error */;
        var useSendBeacon = false;
        this.shutdown(useSendBeacon);
    };
    WebSink.prototype.flushQueue = function () {
        var processingStartTime = getCurrentMilliseconds();
        try {
            var numEvents = this._queue.length;
            if (numEvents) {
                this.stats["pw" /* PassedToWebWorker */] += numEvents;
                var telemetryEventsMessage = {
                    t: 3 /* TelemetryEvents */,
                    d: this._queue
                };
                this._worker.postMessage(telemetryEventsMessage);
            }
        }
        catch (_) {
            logWebSinkWarning(11 /* WorkerPostTelemetryEvent */);
        }
        finally {
            this._queue = [];
            this.stats["md" /* MessagingDuration */] += getCurrentMilliseconds() - processingStartTime;
            // Ensure we don't have multiple timeout loops
            if (this._queueTimeout) {
                clearTimeout(this._queueTimeout);
            }
            this._queueTimeout = setTimeout(this.flushQueue.bind(this), this._batchInterval);
        }
    };
    WebSink.prototype.preprocess = function (telemetryEvent) {
        for (var i = 0; i < this.preprocessors.length; i++) {
            var preprocessor = this.preprocessors[i];
            var preprocessorName = preprocessor.name;
            var success = false;
            var startTime = getCurrentMilliseconds();
            try {
                success = preprocessor.processEvent(telemetryEvent);
            }
            catch (_e) { }
            var preprocessingTime = getCurrentMilliseconds() - startTime;
            this.stats["pd" /* PreprocessingDuration */][preprocessorName] =
                this.stats["pd" /* PreprocessingDuration */][preprocessorName] + preprocessingTime || preprocessingTime;
            if (!success) {
                this.stats["po" /* PreprocessedOut */][preprocessorName] = this.stats["po" /* PreprocessedOut */][preprocessorName] + 1 || 1;
                return false;
            }
        }
        telemetryEvent.dataFields.push(makeStringDataField('OTelJS.Sink', SINK_NAME));
        return true;
    };
    WebSink.prototype.queueEvent = function (event) {
        if (this.stats["s" /* WebWorkerStatus */] === 2 /* Initialized */ || this._queue.length < this._queueSize) {
            this.stats["q" /* Queued */] += 1;
            this._queue.push(event);
        }
        else {
            // We've filled the queue, but we haven't been able to load the web worker.
            // Send events via the MinSink and shut down.
            logWebSinkWarning(3 /* QueueFull */);
            this.stats["s" /* WebWorkerStatus */] = 3 /* QueueFull */;
            var useSendBeacon = false;
            this.shutdown(useSendBeacon);
        }
    };
    /**
     * Queues a telemetry event to send
     * @param telemetryEvent - The telemetry event
     */
    WebSink.prototype.sendTelemetryEvent = function (telemetryEvent) {
        if (this.enabled) {
            if (this.preprocess(telemetryEvent)) {
                this.queueEvent({
                    event: telemetryEvent,
                    t: 1 /* TelemetryEvent */
                });
            }
        }
    };
    /**
     * Queues a customer content event to send
     * @param telemetryEvent - The telemetry event
     */
    WebSink.prototype.sendCustomerContent = function (telemetryEvent) {
        if (this.enabled) {
            if (this.preprocess(telemetryEvent)) {
                this.queueEvent({
                    event: telemetryEvent,
                    t: 2 /* CustomerContentEvent */
                });
            }
        }
    };
    /**
     * Sends any outstanding events via the MinSink, and stops accepting more events
     */
    WebSink.prototype.shutdown = function (useSendBeacon) {
        if (this.isInitialized()) {
            try {
                this.stats["pm" /* PassedToMinSink */] = this._queue.length;
                MinSink.sendEvents(this._props, {
                    uuid: this._utils.uuid,
                    useSendBeacon: useSendBeacon,
                    stats: this.stats
                }, this._queue);
                this._queue = [];
            }
            catch (_e) {
                logWebSinkWarning(30 /* Shutdown */);
            }
        }
        this.enabled = false;
        if (this._queueTimeout) {
            clearTimeout(this._queueTimeout);
        }
    };
    return WebSink;
}());
export { WebSink };
//# sourceMappingURL=WebSink.js.map